import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './tooltip-icon.scss';

interface TooltipInterface {
  text: string;
  showTooltipForce?: boolean;
  infoColor?: 'normal' | 'warning' | 'info' | 'danger' | 'red';
}

/**
 * Info Icon component for displaying tooltip trigger.
 * @returns {React.JSX.Element} The rendered SVG element.
 */
export function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Smart tooltip icon that auto-detects available viewport space and
 * positions the tooltip bubble to the right or left accordingly, rendered via portal.
 * @param {TooltipInterface} properties - The component props.
 * @param {string} properties.text - The text prop.
 * @param {boolean} [properties.showTooltipForce] - The showTooltipForce prop.
 * @param {'normal' | 'warning' | 'info' | 'danger' | 'red'} [properties.infoColor] - The info color prop.
 * @returns {React.ReactElement} The rendered TooltipIcon component.
 */
export function TooltipIcon(properties: TooltipInterface) {
  const { text, showTooltipForce = false, infoColor = 'normal' } = properties;
  const containerReference = useRef<HTMLSpanElement>(null);
  const [direction, setDirection] = useState<'right' | 'left'>('right');
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);

  /**
   * Calculates the positioning coordinates for the tooltip portal based on viewport space.
   */
  const calculateCoords = () => {
    if (!containerReference.current) return;
    const rect = containerReference.current.getBoundingClientRect();
    const spaceOnRight = window.innerWidth - rect.right;
    const tooltipWidth = 300; // matches min-width in SCSS
    setDirection(spaceOnRight >= tooltipWidth + 16 ? 'right' : 'left');
    setCoords({
      x: spaceOnRight >= tooltipWidth + 16 ? rect.right + 10 : rect.left - 10,
      y: rect.top + rect.height / 2,
    });
  };

  /**
   * Handles the mouse enter event to display the tooltip and compute coordinates.
   */
  const handleMouseEnter = () => {
    setIsHovered(true);
    calculateCoords();
  };

  /**
   * Handles the mouse leave event to hide the tooltip.
   */
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    if (showTooltipForce) {
      calculateCoords();
    }
  }, [showTooltipForce]);

  const showTooltip = isHovered || showTooltipForce;

  return (
    <span
      ref={containerReference}
      className={`info-tooltip-container tooltip-${direction} ${showTooltip ? 'show-tooltip' : ''} ${infoColor ? `tooltip-${infoColor}` : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <span className={`info-icon ${infoColor}`}>
        <InfoIcon />
      </span>
      {showTooltip &&
        coords &&
        ReactDOM.createPortal(
          <span
            className={`info-tooltip-text portal-tooltip-${direction} show-tooltip-portal`}
            style={{
              left: coords.x,
              top: coords.y,
            }}>
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
TooltipIcon.defaultProps = {
  showTooltipForce: false,
  infoColor: 'normal',
};
